import PropTypes from "prop-types";
import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import { MSpinner } from "components/Shared";
import { FormIssues, IssuesList, Notifications, RiskProfilerResults } from "./";
import { dateTimeToString } from "components/Shared/Formatters";
import { caseTypes } from "components/Utility";
import Actions from "components/Homeowner/CaseForms/Actions";
import GaarResult from "components/Homeowner/CaseForms/Validations/GaarResults";
import {
  CASE_NOTIFICATIONS,
  CASE_VALIDATION_VIEW,
  CASE_HOLD_UPDATE,
  APPRAISAL_PORT_MESSAGE_ADD,
  DWELLING_BLOCKS_MESSAGE_ADD,
  QA_REJECTION_CARD_VIEW,
  APPRAISAL_UCDP_VIEW,
  APPRAISAL_GAAR_VIEW,
  RISK_PROFILER_VIEW
  , CASE_ISSUES_VIEW
} from "typeConstants/rights";
import { saveVerosPreview, saveVerosSubmit } from "actions/validation";
import { APPRAISAL_INTEGRATION_UCDP, APPRAISAL_INTEGRATION_GAAR, COLLATERAL_ANALYTICS_RISK_PROFILER as COLLATERAL_ANALYTICS_RISK_PROFILER_FEATURE } from "typeConstants/customerFeatures";
import OnHold from "components/Homeowner/CaseForms/Actions/OnHold/index.tsx";
import CaseRejections from "components/Homeowner/CaseForms/Actions/CaseRejections";
import AppraiserCaseIssues from "components/QualityAssurance/Sections/CaseDetails/CaseIssues/Appraiser/AppraiserCaseIssues";
import { isAppraisalCase } from "components/Shared/Functions";

const Validation = ({
  showValidations,
  saveVerosPreview,
  controlNumber,
  refreshMethod,
  saveVerosUcdp,
  validationIssues,
  fetchingValidationIssues,
  FLCview,
  updateControlNumber,
  openCaseActionTab,
  closeTab,
  comparableValidationResult,
  caseForms,
  showNotifications,
  caseType,
  pnxValidation,
  enabledFeatures,
  fetchingEnabledFeatures,
  caseHeader,
  hasOnHoldActionRights,
  rights,
  canViewProfilerResults
  ,showCaseIssues
}) => {
  const mounted = useRef(true);
  const containerRef = useRef();
  const showAppraisalComponents = caseType === caseTypes.valuation && showValidations;
  const showVerosAndUcdp = rights.includes(APPRAISAL_UCDP_VIEW) && enabledFeatures?.map(s => s.feature).includes(APPRAISAL_INTEGRATION_UCDP);
  const showGaar = rights.includes(APPRAISAL_GAAR_VIEW) && enabledFeatures?.map(s => s.feature).includes(APPRAISAL_INTEGRATION_GAAR);  
  const showAppraisalCaseIssues = showAppraisalComponents && showCaseIssues;
  const [qaExpanded, setQaExpanded] = useState(false);
  const appraisalReviewControlMethods = useRef(); //used to toggle the automated appraisal review cards and scroll to them

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  const getReceivedDate = (result) => result?.resultReceivedDate
    ? ("Received " + dateTimeToString(result.resultReceivedDate))
    : "";
  const onSavePreview = (result) => saveVerosPreview(controlNumber, result, refreshMethod);
  const onSaveUcdp = (result) => saveVerosUcdp(controlNumber, result, refreshMethod);

  const setControlMethods = methods => appraisalReviewControlMethods.current = methods;

  const renderPreview = () => {
    if (fetchingValidationIssues) { return <MSpinner />; }
    if (validationIssues.previewStatus === 0) { return null; }
    if (!validationIssues.previewResult) { return null; }
    return (
      <IssuesList
        title="Veros Preview"
        status={validationIssues.previewStatus}
        date={getReceivedDate(validationIssues.previewResult)}
        readOnly={validationIssues.previewResult.readOnly === true}
        items={validationIssues.previewResult.items || []}
        handleSubmit={onSavePreview}
        getControlMethods={setControlMethods}
      />
    );
  };

  const renderUcdp = () => {
    if (fetchingValidationIssues) { return <MSpinner />; }
    if (validationIssues.ucdpStatus === 0) { return null; }
    if (!validationIssues.ucdpResult) { return null; }
    return (
      <IssuesList
        title="UCDP Basic Edit Checks"
        status={validationIssues.ucdpStatus}
        date={getReceivedDate(validationIssues.ucdpResult)}
        readOnly={validationIssues.ucdpResult.readOnly === true}
        items={validationIssues.ucdpResult.items || []}
        handleSubmit={onSaveUcdp} />
    );
  };

  //this method was designed to scroll to either appraisal review card upon closing the spinning dialog, but it doesnt work because the page refresh seems to break it
  //i left it here in case someone ever gets a task to try harder at this because its mostly coded, but maybe needs a timeout or needs to run after the page reloads
  const scrollToAppraisalReviewCard = () => {
    if (appraisalReviewControlMethods.current) {
      appraisalReviewControlMethods.current.openCard();

      if (containerRef.current) {
        const scroll = () => containerRef.current.parentElement.scrollTop = appraisalReviewControlMethods.current.ref.current.offsetTop;

        //we need to scroll a few times or just wait for the card to fully open. it takes about a second to open the card
        //if we set multiple timeouts and keep updating the scroll as the card opens itll be kinda animated
        //33 is fine for a timeout, it simulates a 30hz refresh rate
        for (let i = 0; i < 1200; i += 33) {
          setTimeout(scroll, i);
        }
      }
    }
  };

  if (fetchingEnabledFeatures) return <MSpinner />;

  return (
    <div ref={containerRef}>
      <Actions
        controlNumber={controlNumber}
        refreshMethod={refreshMethod}
        FLCview={FLCview}
        updateControlNumber={updateControlNumber}
        openCaseActionTab={openCaseActionTab}
        closeTab={closeTab}
        comparableValidationResult={comparableValidationResult}
        scrollToAppraisalReviewCard={scrollToAppraisalReviewCard}
      />
      {hasOnHoldActionRights && showValidations &&
        <OnHold
          openCaseActionTab={openCaseActionTab}
          closeTab={closeTab}
        />}
      {showValidations &&
        <FormIssues
          items={pnxValidation?.validationResults}
          isFetching={pnxValidation?.fetchingResult}
          caseForms={caseForms}
          comparableValidationResult={comparableValidationResult}
        />}
      {rights.includes(QA_REJECTION_CARD_VIEW) &&
        <CaseRejections
          qaExpanded={qaExpanded}
          expanderClicked={setQaExpanded}
          controlNumber={controlNumber}
          unresolvedAppraiserRejectionCount={caseHeader.caseInfo?.appraisalSurveyInfo?.unresolvedAppraiserRejectionCount}
          viewOnly={false}
        />
      }
      {/* Show Submit to Risk Profiler button if user has right, enabled feature, and is appraisal */}
      {canViewProfilerResults && isAppraisalCase(caseHeader) && enabledFeatures?.map(s => s.feature).includes(COLLATERAL_ANALYTICS_RISK_PROFILER_FEATURE) && <RiskProfilerResults controlNumber={controlNumber} />}
      {showVerosAndUcdp && renderPreview(fetchingValidationIssues)}
      {showVerosAndUcdp && renderUcdp(fetchingValidationIssues)}
      {showGaar && <GaarResult getControlMethods={setControlMethods}/>}
      {showNotifications && <Notifications controlNumber={controlNumber} />}
      {showAppraisalCaseIssues && <AppraiserCaseIssues controlNumber={controlNumber} />}
    </div>
  );
};

Validation.propTypes = {
  controlNumber: PropTypes.number.isRequired,
  isFetchingFormIssues: PropTypes.bool,
  formIssues: PropTypes.array,
  caseForms: PropTypes.array.isRequired,
  fetchingValidationIssues: PropTypes.bool,
  validationIssues: PropTypes.object,
  saveVerosPreview: PropTypes.func,
  saveVerosUcdp: PropTypes.func,
  caseType: PropTypes.string, // Used to determine if we should show appraisal related validation
  pnxValidation: PropTypes.object,
  refreshMethod: PropTypes.func.isRequired,
  enabledFeatures: PropTypes.array, // Enabled customer features for the control numbers account
  fetchingEnabledFeatures: PropTypes.bool,
  hasAppraisalPortActionRights: PropTypes.bool, // User has one or more rights for Appraisal Port actions
  rights: PropTypes.array,

  // Props for action control
  FLCview: PropTypes.bool,
  updateCaseCallBack: PropTypes.func, // Passed down to actions: used in the callback of convertCaseSurveyType and submitCaseToQa so that the case info updates
  updateControlNumber: PropTypes.func, // Passed down to actions: used to update to display a newly copied case
  showNotifications: PropTypes.bool,
  showValidations: PropTypes.bool,    // User can view case validation info
  openCaseActionTab: PropTypes.func,   // Function to be called when the user clicks an action that opens in a new tab
  closeTab: PropTypes.func,  // Tab control method that is called with a tab id to close a tab opened in Actions
  comparableValidationResult: PropTypes.array,
  caseHeader: PropTypes.object, // Case related properties
  hasOnHoldActionRights: PropTypes.bool,
  canViewProfilerResults: PropTypes.bool
  ,showCaseIssues: PropTypes.bool
};


const mapStateToProps = (state) => {
  return {
    showNotifications: state.user.profile.rights.includes(CASE_NOTIFICATIONS),
    showValidations: state.user.profile.rights.includes(CASE_VALIDATION_VIEW),
    fetchingValidationIssues: state.validation.fetchingValidationIssues,
    validationIssues: state.validation.validationIssues,
    pnxValidation: state.caseForms.pnxValidation,
    enabledFeatures: state.customer.enabledFeatures,
    fetchingEnabledFeatures: state.customer.fetchingEnabledFeatures,
    hasOnHoldActionRights: 
      state.user.profile.rights.includes(CASE_HOLD_UPDATE) || 
      state.user.profile.rights.includes(APPRAISAL_PORT_MESSAGE_ADD) || 
      state.user.profile.rights.includes(DWELLING_BLOCKS_MESSAGE_ADD),
    caseHeader: state.caseForms.caseHeader,
    rights: state.user.profile.rights,
    canViewProfilerResults: state.user.profile.rights.includes(RISK_PROFILER_VIEW)
    ,showCaseIssues: state.user.profile.rights.includes(CASE_ISSUES_VIEW)
  };
};

const mapDispatchToProps = dispatch => ({
  saveVerosPreview(controlIdNumber, verosPreview, callback) {
    dispatch(saveVerosPreview(controlIdNumber, verosPreview, callback));
  },
  saveVerosUcdp(controlIdNumber, verosSubmit, callback) {
    dispatch(saveVerosSubmit(controlIdNumber, verosSubmit, callback));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Validation);
